import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Badge, PageHeader, Modal, Input, Select, Alert } from '../../components/ui';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

export default function Leads() {
  const { user } = useAuthStore();
  const [leads, setLeads]               = useState([]);
  const [lists, setLists]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [showList, setShowList]         = useState(false);
  const [showImport, setShowImport]     = useState(false);
  const [showMapper, setShowMapper]     = useState(false);
  const [showMove, setShowMove]         = useState(false);
  const [showEdit, setShowEdit]         = useState(false);
  const [editLead, setEditLead]         = useState(null);
  const [form, setForm]                 = useState({ email:'',firstName:'',lastName:'',company:'',phone:'',jobTitle:'',listId:'' });
  const [listForm, setListForm]         = useState({ name:'', description:'' });
  const [search, setSearch]             = useState('');
  const [selectedList, setSelectedList] = useState('');
  const [saving, setSaving]             = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult]   = useState(null);
  const [csvHeaders, setCsvHeaders]       = useState([]);
  const [csvRows, setCsvRows]             = useState([]);
  const [csvFile, setCsvFile]             = useState(null);
  const [mapping, setMapping]             = useState({ email:'', firstName:'', lastName:'', company:'', phone:'', jobTitle:'', country:'', city:'' });
  const [selected, setSelected]           = useState(new Set());
  const [moveListId, setMoveListId]       = useState('');
  const [moveAction, setMoveAction]       = useState('move');
  const [importListId, setImportListId]   = useState(''); // FIX: separate from selectedList tab filter
  const [dragOver, setDragOver]           = useState(false);
  const fileRef = useRef();

  useEffect(() => { fetchAll(); }, []);
  // Re-fetch leads whenever the selected list tab changes so we always get
  // the correct set from the server (avoids missing leads due to server-side
  // pagination or limit on the /leads endpoint).
  useEffect(() => { fetchLeads(); }, [selectedList]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [lr, ll] = await Promise.allSettled([
        api.get('/leads', { params: selectedList ? { listId: selectedList } : {} }),
        api.get('/leads/lists'),
      ]);
      if (lr.status==='fulfilled') setLeads(lr.value.data.data || []);
      if (ll.status==='fulfilled') setLists(ll.value.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  // Fetch only leads for the current tab without resetting the lists
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const r = await api.get('/leads', { params: selectedList ? { listId: selectedList } : {} });
      setLeads(r.data.data || []);
    } catch {} finally { setLoading(false); }
  };

  // ── DRAG & DROP ───────────────────────────────
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) {
      processFile(file);
    } else {
      toast.error('Please drop a CSV file');
    }
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  // ── CSV PROCESSING ────────────────────────────
  const processFile = (file) => {
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').filter(Boolean);
      if (!lines.length) return;
      const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g,''));
      const rows = lines.slice(1, 4).map(l => l.split(',').map(c => c.trim().replace(/^"|"$/g,'')));
      setCsvHeaders(headers);
      setCsvRows(rows);
      const detect = (variants) => headers.find(h => variants.some(v => h.toLowerCase().includes(v))) || '';
      setMapping({
        email:     detect(['email']),
        firstName: detect(['first_name','firstname','first name','fname']),
        lastName:  detect(['last_name','lastname','last name','lname','surname']),
        company:   detect(['company','organization','org','business']),
        phone:     detect(['phone','mobile','tel','contact']),
        jobTitle:  detect(['job_title','job title','title','designation','position','role']),
        country:   detect(['country']),
        city:      detect(['city','location']),
      });
      setShowImport(false);
      setShowMapper(true);
    };
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileSelect = (e) => { const f = e.target.files[0]; if (f) processFile(f); };

  const importWithMapping = async () => {
    if (!mapping.email) return toast.error('You must map the Email column');
    if (!csvFile) return toast.error('No file selected');
    setImportLoading(true); setImportResult(null);

    const parseCSV = (str) => {
      const rows = []; let row = []; let field = ''; let inQuotes = false;
      const s = str.replace(/^\uFEFF/, '');
      for (let i = 0; i < s.length; i++) {
        const ch = s[i], next = s[i+1];
        if (inQuotes) {
          if (ch === '"' && next === '"') { field += '"'; i++; }
          else if (ch === '"') { inQuotes = false; }
          else { field += ch; }
        } else {
          if (ch === '"') { inQuotes = true; }
          else if (ch === ',') { row.push(field.trim()); field = ''; }
          else if (ch === '\n' || (ch === '\r' && next === '\n')) {
            if (ch === '\r') i++;
            row.push(field.trim()); if (row.some(v=>v)) rows.push(row); row = []; field = '';
          } else { field += ch; }
        }
      }
      if (field || row.length) { row.push(field.trim()); if (row.some(v=>v)) rows.push(row); }
      return rows;
    };

    const quoteField = (v) => {
      const s = String(v || '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g,'""')}"` : s;
    };

    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = reject;
        reader.readAsText(csvFile);
      });

      const allRows = parseCSV(text);
      if (allRows.length < 2) return toast.error('CSV has no data rows');

      const headers = allRows[0].map(h => h.replace(/^"|"$/g, '').trim());
      const rows = allRows.slice(1).map(vals => {
        const row = {}; headers.forEach((h, i) => { row[h] = vals[i] || ''; }); return row;
      });

      const mapped = rows.map(row => {
        const out = {};
        Object.entries(mapping).forEach(([field, col]) => { if (col) out[field] = row[col] || ''; });
        return out;
      }).filter(r => r.email && r.email.includes('@'));

      if (!mapped.length) return toast.error('No valid email addresses found after mapping');

      const fields = Object.keys(mapped[0]);
      const csvLines = [
        fields.join(','),
        ...mapped.map(r => fields.map(f => quoteField(r[f])).join(','))
      ].join('\n');

      const blob = new Blob([csvLines], { type: 'text/csv' });
      const fd = new FormData();
      fd.append('file', blob, 'mapped.csv');
      if (importListId) fd.append('listId', importListId);

      const r = await api.post('/leads/import-csv', fd);
      setImportResult(r.data);
      toast.success(`Imported ${r.data.imported} leads!`);
      setShowMapper(false); setCsvFile(null); setCsvHeaders([]); setCsvRows([]);
      setImportListId(''); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  // ── LEAD CRUD ─────────────────────────────────
  const addLead = async () => {
    if (!form.email) return toast.error('Email required');
    setSaving(true);
    try { await api.post('/leads', form); toast.success('Lead added!'); setShowAdd(false); setForm({email:'',firstName:'',lastName:'',company:'',phone:'',jobTitle:'',listId:''}); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.message||'Failed'); } finally { setSaving(false); }
  };

  const updateLead = async () => {
    if (!editLead) return;
    setSaving(true);
    try { await api.put(`/leads/${editLead.id}`, editLead); toast.success('Lead updated!'); setShowEdit(false); setEditLead(null); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.message||'Failed'); } finally { setSaving(false); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try { await api.delete(`/leads/${id}`); fetchAll(); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} leads?`)) return;
    try {
      await Promise.all([...selected].map(id => api.delete(`/leads/${id}`)));
      toast.success(`Deleted ${selected.size} leads`);
      setSelected(new Set()); fetchAll();
    } catch { toast.error('Some deletes failed'); }
  };

  const moveSelected = async () => {
    if (!moveListId) return toast.error('Select a list');
    try {
      await api.post('/leads/bulk-action', { ids: [...selected], action: moveAction, listId: moveListId });
      toast.success(`${moveAction === 'move' ? 'Moved' : 'Copied'} ${selected.size} leads`);
      setShowMove(false); setSelected(new Set()); setMoveListId(''); fetchAll();
    } catch (e) { toast.error(e.response?.data?.message||'Failed'); }
  };

  const createList = async () => {
    if (!listForm.name) return toast.error('List name required');
    setSaving(true);
    try { await api.post('/leads/lists', listForm); toast.success('List created!'); setShowList(false); setListForm({name:'',description:''}); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.message||'Failed'); } finally { setSaving(false); }
  };

  const downloadCSV = async () => {
    try { const r = await api.get('/leads/export', { responseType:'blob' }); const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href=url; a.download='leads.csv'; a.click(); }
    catch { toast.error('Export failed'); }
  };

  // ── SELECTION ─────────────────────────────────
  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(l => l.id)));
  };

  const f = (k,v) => setForm(p => ({...p,[k]:v}));

  // FIX: leads are already filtered by list from the API (fetchLeads passes listId param).
  // We only need to apply the search filter here client-side.
  // String() comparison kept as safety net for any cached data.
  const filtered = leads.filter(l => {
    const matchSearch = !search || l.email?.toLowerCase().includes(search.toLowerCase()) || l.firstName?.toLowerCase().includes(search.toLowerCase()) || l.company?.toLowerCase().includes(search.toLowerCase());
    const matchList = !selectedList || String(l.listId) === String(selectedList);
    return matchSearch && matchList;
  });

  const contactsLeft = user?.contactsLimit===-1 ? '∞' : Math.max(0,(user?.contactsLimit||0)-(user?.contactsUsed||0));
  const contactsPct  = user?.contactsLimit > 0 ? Math.min(100,((user?.contactsUsed||0)/user.contactsLimit)*100) : 0;

  return (
    <div>
      <PageHeader title="Leads" subtitle={`${leads.length} contacts · ${contactsLeft} slots remaining`} action={
        <div style={{ display:'flex', gap:8 }}>
          <Button variant="secondary" onClick={downloadCSV} size="sm">⬇ Export</Button>
          <Button variant="secondary" onClick={() => setShowImport(true)}>📂 Import CSV</Button>
          <Button variant="secondary" onClick={() => setShowList(true)}>+ New List</Button>
          <Button onClick={() => setShowAdd(true)}>+ Add Lead</Button>
        </div>
      } />

      {user?.contactsLimit !== -1 && (
        <Card style={{ marginBottom:20, padding:'14px 20px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
            <span style={{ fontSize:13, color:'#888' }}>📋 Contacts Used</span>
            <span style={{ fontSize:13, fontWeight:700, color:contactsPct>=90?'#E74C3C':contactsPct>=70?'#F39C12':'#2ECC71' }}>{user?.contactsUsed?.toLocaleString()} / {user?.contactsLimit?.toLocaleString()}</span>
          </div>
          <div style={{ background:'#1a1a1a', borderRadius:6, height:8 }}>
            <div style={{ width:`${contactsPct}%`, height:'100%', borderRadius:6, background:contactsPct>=90?'#E74C3C':contactsPct>=70?'#F39C12':'#2ECC71', transition:'width 0.5s' }} />
          </div>
          {contactsPct >= 90 && <Alert type="warning" style={{ marginTop:10, padding:'8px 12px' }}>Contacts limit almost full. Upgrade to import more.</Alert>}
        </Card>
      )}

      {/* List filter tabs */}
      {lists.length > 0 && (
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
          <button onClick={()=>setSelectedList('')} style={{ padding:'6px 14px', borderRadius:20, border:`1px solid ${!selectedList?'#2ECC71':'rgba(46,204,113,0.15)'}`, background:!selectedList?'rgba(46,204,113,0.1)':'transparent', color:!selectedList?'#2ECC71':'#666', fontSize:12, cursor:'pointer' }}>All ({leads.length})</button>
          {lists.map(l => (
            <button key={l.id} onClick={()=>setSelectedList(String(l.id))} style={{ padding:'6px 14px', borderRadius:20, border:`1px solid ${String(selectedList)===String(l.id)?'#2ECC71':'rgba(46,204,113,0.15)'}`, background:String(selectedList)===String(l.id)?'rgba(46,204,113,0.1)':'transparent', color:String(selectedList)===String(l.id)?'#2ECC71':'#666', fontSize:12, cursor:'pointer' }}>
              📋 {l.name} ({l.count||0})
            </button>
          ))}
        </div>
      )}

      {/* Search + bulk actions */}
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center' }}>
        <input placeholder="🔍 Search by email, name, company..." value={search} onChange={e=>setSearch(e.target.value)} style={{ flex:1, padding:'10px 14px', background:'#111', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:14, outline:'none' }} />
        {selected.size > 0 && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:13, color:'#2ECC71', fontWeight:700, whiteSpace:'nowrap' }}>{selected.size} selected</span>
            <Button size="sm" variant="secondary" onClick={() => { setMoveAction('move'); setShowMove(true); }}>↗ Move</Button>
            <Button size="sm" variant="secondary" onClick={() => { setMoveAction('copy'); setShowMove(true); }}>📋 Copy</Button>
            <Button size="sm" variant="danger" onClick={deleteSelected}>🗑 Delete</Button>
          </div>
        )}
      </div>

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        filtered.length === 0 ? (
          <Card style={{ textAlign:'center', padding:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>👥</div>
            <h3 style={{ color:'#ccc', marginBottom:8 }}>No leads yet</h3>
            <p style={{ color:'#555', fontSize:14, marginBottom:24 }}>Import a CSV or add leads manually.</p>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <Button variant="secondary" onClick={()=>setShowImport(true)}>📂 Import CSV</Button>
              <Button onClick={()=>setShowAdd(true)}>+ Add Lead</Button>
            </div>
          </Card>
        ) : (
          <Card style={{ padding:0, overflow:'hidden' }}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'rgba(46,204,113,0.05)', borderBottom:'1px solid rgba(46,204,113,0.1)' }}>
                    <th style={{ padding:'11px 14px', width:40 }}>
                      <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} style={{ cursor:'pointer' }} />
                    </th>
                    {['Email','Name','Company','Job Title','Status','Score','Actions'].map(h => (
                      <th key={h} style={{ padding:'11px 14px', textAlign:'left', color:'#555', fontWeight:700, fontSize:11, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(l => (
                    <tr key={l.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.03)', background: selected.has(l.id) ? 'rgba(46,204,113,0.04)' : 'transparent' }}
                      onMouseEnter={e => { if (!selected.has(l.id)) e.currentTarget.style.background='rgba(255,255,255,0.02)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = selected.has(l.id) ? 'rgba(46,204,113,0.04)' : 'transparent'; }}>
                      <td style={{ padding:'10px 14px' }}>
                        <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} style={{ cursor:'pointer' }} />
                      </td>
                      <td style={{ padding:'10px 14px', color:'#2ECC71', fontWeight:600 }}>{l.email}</td>
                      <td style={{ padding:'10px 14px', color:'#ccc' }}>{[l.firstName,l.lastName].filter(Boolean).join(' ')||'—'}</td>
                      <td style={{ padding:'10px 14px', color:'#888' }}>{l.company||'—'}</td>
                      <td style={{ padding:'10px 14px', color:'#888' }}>{l.jobTitle||'—'}</td>
                      <td style={{ padding:'10px 14px' }}><Badge color={l.status==='active'?'success':l.status==='bounced'?'danger':'gray'}>{l.status}</Badge></td>
                      <td style={{ padding:'10px 14px' }}><span style={{ color:l.score>=20?'#2ECC71':l.score>=10?'#F39C12':'#555', fontWeight:700 }}>⭐{l.score||0}</span></td>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          <Button size="sm" variant="secondary" onClick={() => { setEditLead({...l}); setShowEdit(true); }}>✏️</Button>
                          <Button size="sm" variant="danger" onClick={() => deleteLead(l.id)}>🗑</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      )}

      {/* ── IMPORT CSV MODAL ── */}
      <Modal open={showImport} onClose={()=>{setShowImport(false);setImportResult(null);setImportListId('');}} title="Import Leads from CSV">
        <Alert type="info" style={{ marginBottom:16 }}>Upload your CSV — we'll help you map columns on the next step.</Alert>
        {lists.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontSize:13, color:'#888', marginBottom:6 }}>Add to List (optional)</label>
            <select value={importListId} onChange={e=>setImportListId(e.target.value)} style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none' }}>
              <option value="">No list</option>
              {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileRef.current?.click()}
          style={{ border:`2px dashed ${dragOver ? '#2ECC71' : 'rgba(46,204,113,0.25)'}`, borderRadius:14, padding:'40px 36px', textAlign:'center', cursor:'pointer', transition:'all 0.2s', background: dragOver ? 'rgba(46,204,113,0.05)' : 'transparent' }}>
          <div style={{ fontSize:44, marginBottom:10 }}>{dragOver ? '📥' : '📂'}</div>
          <div style={{ fontSize:15, fontWeight:700, color: dragOver ? '#2ECC71' : '#ccc', marginBottom:6 }}>
            {dragOver ? 'Drop your CSV here!' : 'Drag & drop CSV here'}
          </div>
          <div style={{ fontSize:12, color:'#555', marginBottom:12 }}>or click to browse</div>
          <div style={{ display:'inline-block', padding:'8px 20px', background:'rgba(46,204,113,0.1)', border:'1px solid rgba(46,204,113,0.3)', borderRadius:8, fontSize:13, color:'#2ECC71', fontWeight:600 }}>Choose File</div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={handleFileSelect} />
        </div>
        <div style={{ marginTop:12, fontSize:12, color:'#555' }}>
          <button onClick={(e)=>{ e.stopPropagation(); const a=document.createElement('a'); a.href='data:text/csv;charset=utf-8,email,first_name,last_name,company,phone,job_title\njohn@company.com,John,Smith,Acme Inc,+1234567890,CEO'; a.download='sample-leads.csv'; a.click(); }} style={{ background:'none', border:'none', color:'#2ECC71', cursor:'pointer', fontSize:12 }}>⬇ Download sample CSV</button>
        </div>
      </Modal>

      {/* ── COLUMN MAPPER MODAL ── */}
      <Modal open={showMapper} onClose={()=>setShowMapper(false)} title="Map CSV Columns" size="lg">
        <Alert type="info" style={{ marginBottom:16 }}>Select which column maps to each field. Only <strong>Email</strong> is required.</Alert>
        {csvRows.length > 0 && (
          <div style={{ marginBottom:20, overflowX:'auto' }}>
            <div style={{ fontSize:12, color:'#555', marginBottom:6 }}>Preview (first 3 rows):</div>
            <table style={{ borderCollapse:'collapse', fontSize:11, width:'100%' }}>
              <thead><tr>{csvHeaders.map(h => <th key={h} style={{ padding:'6px 10px', background:'rgba(46,204,113,0.08)', border:'1px solid rgba(46,204,113,0.1)', color:'#2ECC71', fontWeight:700, whiteSpace:'nowrap' }}>{h}</th>)}</tr></thead>
              <tbody>{csvRows.map((row,i) => <tr key={i}>{csvHeaders.map((h,j) => <td key={j} style={{ padding:'5px 10px', border:'1px solid rgba(255,255,255,0.05)', color:'#888', whiteSpace:'nowrap' }}>{row[j]||'—'}</td>)}</tr>)}</tbody>
            </table>
          </div>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
          {[['email','Email *'],['firstName','First Name'],['lastName','Last Name'],['company','Company'],['phone','Phone'],['jobTitle','Job Title'],['country','Country'],['city','City']].map(([field, label]) => (
            <div key={field}>
              <label style={{ display:'block', fontSize:12, color:field==='email'?'#2ECC71':'#888', fontWeight:field==='email'?700:400, marginBottom:5 }}>{label}</label>
              <select value={mapping[field]||''} onChange={e=>setMapping(p=>({...p,[field]:e.target.value}))} style={{ width:'100%', padding:'8px 12px', background:'#1a1a1a', border:`1px solid ${field==='email'&&!mapping.email?'#E74C3C':'rgba(46,204,113,0.15)'}`, borderRadius:8, color:'#fff', fontSize:13, outline:'none' }}>
                <option value="">— Skip —</option>
                {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          ))}
        </div>
        {importResult && (
          <div style={{ background:'rgba(46,204,113,0.06)', border:'1px solid rgba(46,204,113,0.2)', borderRadius:12, padding:14, marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#2ECC71', marginBottom:8 }}>✅ Import Complete</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
              {[['Imported',importResult.imported,'#2ECC71'],['Duplicates',importResult.duplicates,'#F39C12'],['Errors',importResult.errors,'#E74C3C'],['Skipped',importResult.skipped,'#888']].map(([l,v,c]) => (
                <div key={l} style={{ textAlign:'center', background:'#1a1a1a', borderRadius:8, padding:'10px 6px' }}>
                  <div style={{ fontSize:18, fontWeight:800, color:c }}>{v}</div>
                  <div style={{ fontSize:11, color:'#555', marginTop:2 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display:'flex', gap:10 }}>
          <Button variant="secondary" onClick={()=>setShowMapper(false)} style={{ flex:1 }}>Cancel</Button>
          <Button loading={importLoading} onClick={importWithMapping} disabled={!mapping.email} style={{ flex:2 }}>📥 Import with This Mapping</Button>
        </div>
      </Modal>

      {/* ── MOVE/COPY MODAL ── */}
      <Modal open={showMove} onClose={()=>setShowMove(false)} title={moveAction==='move'?'Move Leads to List':'Copy Leads to List'} size="sm">
        <p style={{ color:'#888', fontSize:14, marginBottom:16 }}>
          {moveAction==='move'?'Move':'Copy'} <strong style={{ color:'#2ECC71' }}>{selected.size} leads</strong> to:
        </p>
        <div style={{ marginBottom:8 }}>
          <label style={{ display:'block', fontSize:13, color:'#888', marginBottom:6 }}>Select List</label>
          <select value={moveListId} onChange={e=>setMoveListId(e.target.value)} style={{ width:'100%', padding:'10px 14px', background:'#1a1a1a', border:'1px solid rgba(46,204,113,0.15)', borderRadius:10, color:'#fff', fontSize:13, outline:'none' }}>
            <option value="">— Choose a list —</option>
            {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          {['move','copy'].map(a => (
            <button key={a} onClick={()=>setMoveAction(a)} style={{ flex:1, padding:'8px', borderRadius:8, border:`1px solid ${moveAction===a?'#2ECC71':'rgba(46,204,113,0.15)'}`, background:moveAction===a?'rgba(46,204,113,0.1)':'transparent', color:moveAction===a?'#2ECC71':'#666', cursor:'pointer', fontSize:13, fontWeight:600 }}>
              {a==='move'?'↗ Move':'📋 Copy'}
            </button>
          ))}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <Button variant="secondary" onClick={()=>setShowMove(false)} style={{ flex:1 }}>Cancel</Button>
          <Button onClick={moveSelected} disabled={!moveListId} style={{ flex:2 }}>{moveAction==='move'?'Move':'Copy'} Leads</Button>
        </div>
      </Modal>

      {/* ── EDIT LEAD MODAL ── */}
      <Modal open={showEdit} onClose={()=>{setShowEdit(false);setEditLead(null);}} title="Edit Lead">
        {editLead && <>
          <Input label="Email *" type="email" value={editLead.email||''} onChange={e=>setEditLead(p=>({...p,email:e.target.value}))} />
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <Input label="First Name" value={editLead.firstName||''} onChange={e=>setEditLead(p=>({...p,firstName:e.target.value}))} />
            <Input label="Last Name" value={editLead.lastName||''} onChange={e=>setEditLead(p=>({...p,lastName:e.target.value}))} />
          </div>
          <Input label="Company" value={editLead.company||''} onChange={e=>setEditLead(p=>({...p,company:e.target.value}))} />
          <Input label="Job Title" value={editLead.jobTitle||''} onChange={e=>setEditLead(p=>({...p,jobTitle:e.target.value}))} />
          <Input label="Phone" value={editLead.phone||''} onChange={e=>setEditLead(p=>({...p,phone:e.target.value}))} />
          {lists.length>0 && (
            <Select label="Move to List" value={editLead.listId||''} onChange={e=>setEditLead(p=>({...p,listId:e.target.value}))}>
              <option value="">No list</option>
              {lists.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
            </Select>
          )}
          <div style={{ display:'flex', gap:10, marginTop:8 }}>
            <Button variant="secondary" onClick={()=>{setShowEdit(false);setEditLead(null);}} style={{ flex:1 }}>Cancel</Button>
            <Button loading={saving} onClick={updateLead} style={{ flex:2 }}>Save Changes</Button>
          </div>
        </>}
      </Modal>

      {/* ── ADD LEAD MODAL ── */}
      <Modal open={showAdd} onClose={()=>setShowAdd(false)} title="Add Lead">
        <Input label="Email *" type="email" value={form.email} onChange={e=>f('email',e.target.value)} />
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Input label="First Name" value={form.firstName} onChange={e=>f('firstName',e.target.value)} />
          <Input label="Last Name" value={form.lastName} onChange={e=>f('lastName',e.target.value)} />
        </div>
        <Input label="Company" value={form.company} onChange={e=>f('company',e.target.value)} />
        <Input label="Job Title" value={form.jobTitle} onChange={e=>f('jobTitle',e.target.value)} />
        <Input label="Phone" value={form.phone} onChange={e=>f('phone',e.target.value)} />
        {lists.length>0&&<Select label="Add to List" value={form.listId} onChange={e=>f('listId',e.target.value)}><option value="">No list</option>{lists.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}</Select>}
        <div style={{ display:'flex', gap:10, marginTop:8 }}>
          <Button variant="secondary" onClick={()=>setShowAdd(false)} style={{ flex:1 }}>Cancel</Button>
          <Button loading={saving} onClick={addLead} style={{ flex:2 }}>Add Lead</Button>
        </div>
      </Modal>

      {/* ── CREATE LIST MODAL ── */}
      <Modal open={showList} onClose={()=>setShowList(false)} title="Create Lead List" size="sm">
        <Input label="List Name *" placeholder="e.g. Q1 Prospects" value={listForm.name} onChange={e=>setListForm(p=>({...p,name:e.target.value}))} />
        <Input label="Description" placeholder="Optional" value={listForm.description} onChange={e=>setListForm(p=>({...p,description:e.target.value}))} />
        <div style={{ display:'flex', gap:10, marginTop:8 }}>
          <Button variant="secondary" onClick={()=>setShowList(false)} style={{ flex:1 }}>Cancel</Button>
          <Button loading={saving} onClick={createList} style={{ flex:2 }}>Create List</Button>
        </div>
      </Modal>
    </div>
  );
}
